import { useEffect, useMemo, useState, useRef } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Search, X } from "lucide-react";
import { api } from "@/lib/api";
import { useT, usePageTitle, Trans } from "@/i18n/LanguageContext";
import ProductCard from "@/components/ProductCard";

export default function Collections() {
    const t = useT();
    const { slug } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();

    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    const gridRef = useRef(null);

    const goToPage = (n) => {
        setPage(n);
        gridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const categorySlug = slug || searchParams.get("category") || "";
    const room = searchParams.get("room") || "";
    const material = searchParams.get("material") || "";
    const style = searchParams.get("style") || "";
    const q = searchParams.get("q") || "";
    const [searchValue, setSearchValue] = useState(q);

    // Tab title follows the selected collection, falling back to the catalog title.
    usePageTitle(
        categories.find((c) => c.slug === categorySlug)?.name || t("meta.title.collections")
    );

    // Scroll anchoring: the plain catalog (/collections) lands at the top with the
    // full header; arriving on a specific collection — from the homepage cards or a
    // direct link — anchors at the tab bar, same as switching category tabs.
    const filterRef = useRef(null);
    const prevSlugRef = useRef(null); // null = not mounted yet ("" is a valid slug value)
    useEffect(() => {
        if (prevSlugRef.current === categorySlug) return;
        const isFirstMount = prevSlugRef.current === null;
        prevSlugRef.current = categorySlug;
        if (isFirstMount && !categorySlug) return;
        filterRef.current?.scrollIntoView({ block: "start" });
    }, [categorySlug]);

    // Keep the selected chip in view on the horizontal rail (phones). Scrolling the
    // rail directly rather than scrollIntoView so the page never moves vertically.
    const railRef = useRef(null);
    useEffect(() => {
        const rail = railRef.current;
        const chip = rail?.querySelector('[data-active="true"]');
        if (!rail || !chip) return;
        rail.scrollTo({
            left: chip.offsetLeft - rail.clientWidth / 2 + chip.clientWidth / 2,
            behavior: "smooth",
        });
    }, [categorySlug, categories.length]);

    useEffect(() => { setSearchValue(q); }, [q]);

    useEffect(() => {
        api.get("/categories", { params: { top_level: true } })
            .then((r) => setCategories(r.data))
            .catch(() => {});
    }, []);

    useEffect(() => {
        setLoading(true);
        const params = { visible: true, page, page_size: pageSize };
        if (categorySlug) params.category = categorySlug;
        if (room) params.room = room;
        if (material) params.material = material;
        if (style) params.style = style;
        if (q) params.q = q;
        api
            .get("/products", { params })
            .then((r) => {
                setProducts(r.data.items ?? []);
                setTotal(r.data.total ?? 0);
            })
            .catch(() => {
                setProducts([]);
                setTotal(0);
            })
            .finally(() => setLoading(false));
    }, [categorySlug, room, material, style, q, page, pageSize]);

    // Reset page when filters change
    useEffect(() => { setPage(1); }, [categorySlug, room, material, style, q, pageSize]);

    const rooms = useMemo(() => {
        const s = new Set(products.map((p) => p.room).filter(Boolean));
        return Array.from(s);
    }, [products]);

    const materials = useMemo(() => {
        const s = new Set();
        products.forEach((p) => (p.materials || []).forEach((m) => s.add(m)));
        return Array.from(s);
    }, [products]);

    const setParam = (key, value) => {
        const next = new URLSearchParams(searchParams);
        if (value) next.set(key, value);
        else next.delete(key);
        setSearchParams(next);
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setParam("q", searchValue.trim());
    };

    const clearSearch = () => {
        setSearchValue("");
        setParam("q", "");
    };

    return (
        <>
            <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 md:pt-28 pb-12" data-testid="catalog-header">
                <p className="overline reveal">{t("cat.overline")}</p>
                <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl mt-5 leading-[0.95] reveal reveal-delay-1">
                    <Trans tKey="cat.title" />
                </h1>
                <p className="text-melilux-cream/65 max-w-xl mt-8 leading-relaxed reveal reveal-delay-2">
                    {t("cat.lead")}
                </p>
            </section>

            {/* Search */}
            <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-6" data-testid="catalog-search-section">
                <form onSubmit={handleSearch} className="flex items-center gap-3 border-b border-white/15 focus-within:border-melilux-gold transition-colors py-2">
                    <Search size={16} className="text-melilux-cream/55 shrink-0" strokeWidth={1.5} />
                    <input
                        type="text"
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        placeholder={t("cat.search.placeholder")}
                        data-testid="catalog-search-input"
                        className="flex-1 bg-transparent text-melilux-cream placeholder:text-melilux-cream/40 outline-none text-sm py-2"
                    />
                    {searchValue && (
                        <button type="button" onClick={clearSearch} data-testid="catalog-search-clear" className="text-melilux-cream/50 hover:text-melilux-gold transition-colors">
                            <X size={15} strokeWidth={1.5} />
                        </button>
                    )}
                    <button type="submit" data-testid="catalog-search-submit" className="text-[0.7rem] uppercase tracking-[0.28em] text-melilux-gold hover:text-melilux-goldBright transition-colors">
                        {t("cat.search.submit")}
                    </button>
                </form>
                {q && (
                    <p className="overline mt-3" data-testid="catalog-search-active">
                        {t("cat.search.active", { q })}
                    </p>
                )}
            </section>

            {/* Categories — a wrapping row on desktop, a swipeable rail stuck under the
                header on phones so switching collection never means scrolling a long list */}
            <section
                ref={filterRef}
                className="sticky top-[calc(5rem_+_env(safe-area-inset-top))] z-30 bg-melilux-bg border-b border-white/10 scroll-mt-20 md:static md:z-auto"
                data-testid="category-filter"
            >
                <div
                    ref={railRef}
                    className="relative max-w-7xl mx-auto flex gap-2 overflow-x-auto scrollbar-hide rail-fade snap-x px-6 lg:px-10 py-4 md:py-0 md:pb-10 md:flex-wrap md:overflow-visible"
                >
                    {[{ id: "__all", slug: "", name: t("cat.all") }, ...categories].map((c) => {
                        const active = categorySlug === c.slug;
                        return (
                            <Link
                                key={c.id}
                                to={c.slug ? `/collections/${c.slug}` : "/collections"}
                                data-testid={c.slug ? `cat-${c.slug}` : "cat-all"}
                                data-active={active}
                                className={`shrink-0 snap-start inline-flex items-center min-h-[40px] px-4 text-[0.72rem] uppercase tracking-[0.28em] border transition-colors ${
                                    active
                                        ? "bg-melilux-gold border-melilux-gold text-melilux-bg"
                                        : c.highlighted
                                            ? "border-melilux-gold text-melilux-gold hover:bg-melilux-gold hover:text-melilux-bg"
                                            : "border-white/15 text-melilux-cream/65 hover:border-melilux-cream/40 hover:text-melilux-cream"
                                }`}
                            >
                                {c.name}
                            </Link>
                        );
                    })}
                </div>
            </section>

            {/* Sub-filters */}
            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row md:flex-wrap md:items-center gap-4 md:gap-6 text-[0.7rem] uppercase tracking-[0.25em]">
                <div className="flex flex-wrap items-center gap-3" data-testid="style-filter">
                    <span className="text-melilux-cream/45">{t("cat.style")}</span>
                    {["", "Classic", "Modern"].map((s) => (
                        <button
                            key={s || "all"}
                            onClick={() => setParam("style", s)}
                            data-testid={`style-${s || "all"}`}
                            className={`inline-flex items-center min-h-[36px] px-3.5 border transition-colors ${
                                style === s
                                    ? "border-melilux-gold text-melilux-gold"
                                    : "border-white/15 text-melilux-cream/65 hover:border-melilux-cream/40 hover:text-melilux-cream"
                            }`}
                        >
                            {s ? t(`style.${s.toLowerCase()}`) : t("cat.all")}
                        </button>
                    ))}
                </div>
                {(rooms.length > 0 || room) && (
                    <div className="flex items-center gap-3" data-testid="room-filter">
                        <span className="text-melilux-cream/45 shrink-0">{t("cat.room")}</span>
                        <select
                            value={room}
                            onChange={(e) => setParam("room", e.target.value)}
                            data-testid="room-select"
                            className="flex-1 min-w-0 md:flex-none bg-melilux-bg border border-white/15 px-3 min-h-[36px] text-melilux-cream uppercase tracking-[0.2em] text-[0.7rem] focus:border-melilux-gold outline-none"
                        >
                            <option value="">{t("cat.all")}</option>
                            {rooms.map((r) => (<option key={r} value={r}>{r}</option>))}
                        </select>
                    </div>
                )}
                {(materials.length > 0 || material) && (
                    <div className="flex items-center gap-3" data-testid="material-filter">
                        <span className="text-melilux-cream/45 shrink-0">{t("cat.material")}</span>
                        <select
                            value={material}
                            onChange={(e) => setParam("material", e.target.value)}
                            data-testid="material-select"
                            className="flex-1 min-w-0 md:flex-none bg-melilux-bg border border-white/15 px-3 min-h-[36px] text-melilux-cream uppercase tracking-[0.2em] text-[0.7rem] focus:border-melilux-gold outline-none"
                        >
                            <option value="">{t("cat.all")}</option>
                            {materials.map((m) => (<option key={m} value={m}>{m}</option>))}
                        </select>
                    </div>
                )}
                {(room || material || style) && (
                    <button
                        data-testid="clear-filters"
                        className="self-start text-melilux-gold link-underline"
                        onClick={() => setSearchParams(q ? { q } : {})}
                    >
                        {t("cat.clear")}
                    </button>
                )}
            </section>

            {/* Grid */}
            {/* scroll-mt clears the fixed header plus the sticky category rail on phones */}
            <section ref={gridRef} className="max-w-7xl mx-auto px-6 lg:px-10 pb-12 mt-6 scroll-mt-40 md:scroll-mt-24" data-testid="products-grid">
                {loading ? (
                    <div className="py-32 text-center text-melilux-cream/50 text-[0.7rem] uppercase tracking-[0.3em]">
                        {t("cat.loading")}
                    </div>
                ) : products.length === 0 ? (
                    <div className="py-32 text-center text-melilux-cream/50" data-testid="empty-state">
                        <p className="font-serif text-3xl">{t("cat.empty.title")}</p>
                        <p className="text-sm mt-3">{t("cat.empty.lead")}</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-5 gap-y-10">
                        {products.map((p) => (
                            <ProductCard key={p.id} p={p} />
                        ))}
                    </div>
                )}

                {/* Pagination — inside the section, after the grid */}
                {total > 0 && !loading && (
                    <div className="mt-12 flex flex-col-reverse items-center gap-6 md:flex-row md:justify-between md:gap-4 text-[0.7rem] uppercase tracking-[0.25em] text-melilux-cream/55">
                        <div className="flex items-center gap-3">
                            <span>{t("cat.show")}</span>
                            {[20, 60, 100].map((n) => (
                                <button
                                    key={n}
                                    onClick={() => setPageSize(n)}
                                    className={`inline-flex items-center min-h-[36px] px-3.5 border transition-colors ${
                                        pageSize === n
                                            ? "border-melilux-gold text-melilux-gold"
                                            : "border-white/15 hover:border-melilux-cream/40"
                                    }`}
                                >
                                    {n}
                                </button>
                            ))}
                        </div>
                        <span>
              {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} {t("cat.of")} {total}
            </span>
                        <div className="flex gap-2 w-full md:w-auto">
                            <button
                                onClick={() => goToPage(Math.max(1, page - 1))}
                                disabled={page === 1}
                                className="flex-1 md:flex-none inline-flex items-center justify-center min-h-[44px] md:min-h-[36px] px-6 border border-white/15 hover:border-melilux-gold hover:text-melilux-gold disabled:opacity-30 transition-colors"
                            >
                                ←
                            </button>
                            <button
                                onClick={() => goToPage(page + 1)}
                                disabled={page * pageSize >= total}
                                className="flex-1 md:flex-none inline-flex items-center justify-center min-h-[44px] md:min-h-[36px] px-6 border border-white/15 hover:border-melilux-gold hover:text-melilux-gold disabled:opacity-30 transition-colors"
                            >
                                →
                            </button>
                        </div>
                    </div>
                )}
            </section>
        </>
    );
}